import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useSession } from '../contexts/SessionContext';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import { LoadingSpinner } from '../components/LoadingSpinner';
import { FocusHeader } from '../components/FocusHeader';
import { SEOHead } from '../components/SEOHead';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Underline from '@tiptap/extension-underline';
import Highlight from '@tiptap/extension-highlight';
import { FeaturedImageManager } from '../components/FeaturedImageManager';
import {
  useEditorImages,
  blogImageExtensions,
  editorImageClickHandler,
  noEditorImageHandlers,
  type EditorImageHandlers,
} from '../hooks/useEditorImages';
import { EditorImageTools } from '../components/EditorImageTools';
import {
  Bold,
  Italic,
  Underline as UnderlineIcon,
  List,
  ListOrdered,
  Link2,
  Link2Off,
  ImagePlus,
  LayoutGrid,
  Heading1,
  Heading2,
  Heading3,
  Highlighter,
  Quote,
  Undo,
  Redo,
  ChevronDown,
  X
} from 'lucide-react';

interface BlogData {
  id: string;
  user_id: string;
  slug: string;
  title: string;
  intro: string;
  content: string;
  featured_image_url: string | null;
  status: string;
}

interface SEOData {
  id: string;
  blog_id: string;
  meta_title: string;
  meta_description: string;
  meta_keywords: string;
  og_title: string;
  og_description: string;
  og_image: string | null;
  twitter_card: string;
  twitter_title: string;
  twitter_description: string;
  twitter_image: string | null;
  canonical_url: string;
}

export function BlogEdit() {
  const { blogId } = useParams<{ blogId: string }>();
  const navigate = useNavigate();
  const { user: authUser } = useAuth();
  const { user: sessionUser } = useSession();
  const user = authUser || sessionUser;

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [blog, setBlog] = useState<BlogData | null>(null);
  const [seo, setSeo] = useState<SEOData | null>(null);
  const [error, setError] = useState('');
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const [showSEOSection, setShowSEOSection] = useState(false);

  const [title, setTitle] = useState('');
  const [intro, setIntro] = useState('');
  const [featuredImage, setFeaturedImage] = useState('');
  const [metaTitle, setMetaTitle] = useState('');
  const [metaDescription, setMetaDescription] = useState('');
  const [metaKeywords, setMetaKeywords] = useState('');
  const [ogTitle, setOgTitle] = useState('');
  const [ogDescription, setOgDescription] = useState('');
  const [ogImage, setOgImage] = useState('');
  const [twitterTitle, setTwitterTitle] = useState('');
  const [twitterDescription, setTwitterDescription] = useState('');
  const [twitterImage, setTwitterImage] = useState('');
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [activeHighlightColor, setActiveHighlightColor] = useState('#fef08a');
  const colorPickerRef = useRef<HTMLDivElement>(null);

  const [showLinkModal, setShowLinkModal] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');
  const [linkText, setLinkText] = useState('');
  const [isEditingExistingLink, setIsEditingExistingLink] = useState(false);

  const autoSaveTimeoutRef = useRef<NodeJS.Timeout>();
  // De editor bestaat eerder dan de hook; via deze ref bereikt de editor-config de foto-functies.
  const imageHandlersRef = useRef<EditorImageHandlers>(noEditorImageHandlers);
  const titleIntroLoadedRef = useRef(false);

  const highlightColors = [
    { name: 'Geel', color: '#fef08a' },
    { name: 'Groen', color: '#bbf7d0' },
    { name: 'Blauw', color: '#bfdbfe' },
    { name: 'Oranje', color: '#fed7aa' },
    { name: 'Roze', color: '#fbcfe8' },
    { name: 'Lavendel', color: '#e9d5ff' },
    { name: 'Koraal', color: '#fecaca' },
    { name: 'Grijs', color: '#e5e7eb' },
  ];

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        link: false,
      }),
      Underline,
      Highlight.configure({
        multicolor: true,
      }),
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          class: 'text-[#FF6B35] underline hover:text-[#E55A2B]',
        },
      }),
      ...blogImageExtensions(imageHandlersRef),
    ],
    content: '',
    editorProps: {
      attributes: {
        class: 'prose prose-lg max-w-none focus:outline-none min-h-[400px] px-4 py-3',
      },
      handleClickOn: editorImageClickHandler(imageHandlersRef),
    },
    onUpdate: () => {
      scheduleAutoSave();
    },
  });

  const images = useEditorImages({
    editor,
    userId: user?.id,
    target: blogId ? { context: 'blog', blogId } : null,
    handlersRef: imageHandlersRef,
    onError: setError,
    // scheduleAutoSave staat verderop in dit bestand; via deze omweg wordt hij pas bij aanroep opgezocht.
    onChanged: () => scheduleAutoSave(),
  });

  useEffect(() => {
    if (!user || !blogId) {
      navigate('/my-blogs');
      return;
    }
    loadBlogData();
  }, [user, blogId]);

  useEffect(() => {
    const savedColor = localStorage.getItem('preferredHighlightColor');
    if (savedColor) {
      setActiveHighlightColor(savedColor);
    }
  }, []);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (colorPickerRef.current && !colorPickerRef.current.contains(event.target as Node)) {
        setShowColorPicker(false);
      }
    }

    if (showColorPicker) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [showColorPicker]);

  const loadBlogData = async () => {
    try {
      setLoading(true);
      setError('');

      const { data: blogData, error: blogError } = await supabase
        .from('published_blogs')
        .select('*')
        .eq('id', blogId)
        .eq('user_id', user!.id)
        .maybeSingle();

      if (blogError || !blogData) {
        setError('Blog niet gevonden of je hebt geen toegang');
        setLoading(false);
        return;
      }

      setBlog(blogData);
      setTitle(blogData.title);
      setIntro(blogData.intro);
      setFeaturedImage(blogData.featured_image_url || '');

      if (editor) {
        editor.commands.setContent(blogData.content);
      }

      const { data: seoData, error: seoError } = await supabase
        .from('blog_seo_metadata')
        .select('*')
        .eq('blog_id', blogId)
        .maybeSingle();

      if (seoData) {
        setSeo(seoData);
        setMetaTitle(seoData.meta_title);
        setMetaDescription(seoData.meta_description);
        setMetaKeywords(seoData.meta_keywords || '');
        setOgTitle(seoData.og_title);
        setOgDescription(seoData.og_description);
        setOgImage(seoData.og_image || '');
        setTwitterTitle(seoData.twitter_title);
        setTwitterDescription(seoData.twitter_description);
        setTwitterImage(seoData.twitter_image || '');
      }

      setLoading(false);
    } catch (err: any) {
      console.error('Error loading blog:', err);
      setError(err.message);
      setLoading(false);
    }
  };

  useEffect(() => {
    if (blog && editor && !editor.isDestroyed) {
      editor.commands.setContent(blog.content);
    }
  }, [blog, editor]);

  useEffect(() => {
    if (!blog) return;
    if (!titleIntroLoadedRef.current) {
      titleIntroLoadedRef.current = true;
      return;
    }
    scheduleAutoSave();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [title, intro]);

  const scheduleAutoSave = () => {
    if (autoSaveTimeoutRef.current) {
      clearTimeout(autoSaveTimeoutRef.current);
    }

    autoSaveTimeoutRef.current = setTimeout(() => {
      handleAutoSave();
    }, 3000);
  };

  const handleAutoSave = async () => {
    // Ook niet opslaan vanuit een verlaten scherm: de editor is dan al afgebroken.
    if (!blog || !editor || editor.isDestroyed) return;

    if (!title.trim() || !intro.trim()) {
      scheduleAutoSave();
      return;
    }

    if (!images.prepareForSave()) {
      scheduleAutoSave();
      return;
    }

    try {
      const content = editor.getHTML();

      await supabase
        .from('published_blogs')
        .update({
          title,
          intro,
          content,
          featured_image_url: featuredImage || null,
          updated_at: new Date().toISOString(),
        })
        .eq('id', blog.id);

      setLastSaved(new Date());
    } catch (err) {
      console.error('Auto-save error:', err);
    }
  };

  const handleSave = async () => {
    if (!blog || !editor) return;

    if (!title.trim() || !intro.trim()) {
      setError('Titel en intro zijn verplicht');
      return;
    }

    if (!images.prepareForSave()) {
      setError('Nog even geduld: een afbeelding wordt nog geüpload.');
      return;
    }

    try {
      setSaving(true);
      setError('');

      const content = editor.getHTML();

      const { error: blogError } = await supabase
        .from('published_blogs')
        .update({
          title,
          intro,
          content,
          featured_image_url: featuredImage || null,
          updated_at: new Date().toISOString(),
        })
        .eq('id', blog.id);

      if (blogError) throw blogError;

      if (seo) {
        const { error: seoError } = await supabase
          .from('blog_seo_metadata')
          .update({
            meta_title: metaTitle,
            meta_description: metaDescription,
            meta_keywords: metaKeywords,
            og_title: ogTitle,
            og_description: ogDescription,
            og_image: ogImage || null,
            twitter_title: twitterTitle,
            twitter_description: twitterDescription,
            twitter_image: twitterImage || null,
            updated_at: new Date().toISOString(),
          })
          .eq('id', seo.id);

        if (seoError) throw seoError;
      }

      setLastSaved(new Date());
      navigate('/my-blogs');
    } catch (err: any) {
      console.error('Error saving blog:', err);
      setError('Er ging iets mis bij het opslaan: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const openLinkModal = () => {
    if (!editor) return;

    const existingHref = editor.getAttributes('link').href || '';

    let { from, to } = editor.state.selection;
    if (from === to && existingHref) {
      editor.commands.extendMarkRange('link');
      ({ from, to } = editor.state.selection);
    }

    const selectedText = editor.state.doc.textBetween(from, to, '');

    setLinkUrl(existingHref);
    setLinkText(selectedText);
    setIsEditingExistingLink(!!existingHref);
    setShowLinkModal(true);
  };

  const handleLinkSave = () => {
    if (!editor) return;

    if (!linkUrl.trim()) {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
    } else {
      const url = linkUrl.trim().startsWith('http') ? linkUrl.trim() : `https://${linkUrl.trim()}`;
      editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
    }

    setShowLinkModal(false);
  };

  const handleLinkRemove = () => {
    if (!editor) return;
    editor.chain().focus().unsetLink().run();
    setShowLinkModal(false);
  };

  const applyHighlight = (color: string) => {
    if (editor) {
      editor.chain().focus().setHighlight({ color }).run();
      setActiveHighlightColor(color);
      localStorage.setItem('preferredHighlightColor', color);
      setShowColorPicker(false);
    }
  };

  const removeHighlight = () => {
    if (editor) {
      editor.chain().focus().unsetHighlight().run();
      setShowColorPicker(false);
    }
  };


  useEffect(() => {
    return () => {
      if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current);
      }
    };
  }, []);

  const hasUnsavedChanges = Boolean(!lastSaved && (title.trim() || intro.trim() || (editor && editor.getHTML() !== blog?.content)));

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F2F2EF]">
        <LoadingSpinner text="Blog laden..." />
      </div>
    );
  }

  if (error && !blog) {
    return (
      <div className="min-h-screen bg-[#F2F2EF] flex items-center justify-center">
        <div className="card max-w-2xl mx-auto text-center">
          <p className="text-red-600 mb-4">{error}</p>
          <button onClick={() => navigate('/my-blogs')} className="btn-primary">
            Terug naar mijn blogs
          </button>
        </div>
      </div>
    );
  }

  if (!editor) {
    return (
      <div className="min-h-screen bg-[#F2F2EF]">
        <LoadingSpinner text="Editor laden..." />
      </div>
    );
  }

  return (
    <>
      <SEOHead
        title={title ? `Bewerk ${title} - dibbi` : 'Bewerk blog - dibbi'}
        description="Bewerk je blog en sla je wijzigingen op"
        url={typeof window !== 'undefined' ? window.location.href : 'https://app.dibbi.nl'}
      />
      <div className="min-h-screen bg-[#F2F2EF]">
        <FocusHeader
          title="Blog bewerken"
          onBack={() => navigate('/my-blogs')}
          onCancel={() => navigate('/my-blogs')}
          onSave={handleSave}
          isSaving={saving}
          saveDisabled={!title.trim() || !intro.trim()}
          hasUnsavedChanges={hasUnsavedChanges}
        />

        <div className="max-w-5xl mx-auto space-y-6 px-4 py-8">

        {error && (
          <div className="card bg-red-50 border-red-200">
            <p className="text-red-600">{error}</p>
          </div>
        )}

        <div className="card space-y-6">
          <div>
            <h2 className="text-xl font-semibold text-[#231F20] mb-4">Blog Gegevens</h2>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Titel <span className="text-red-500">*</span>
                  <span className="text-gray-500 font-normal ml-2">({title.length} karakters)</span>
                </label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  className="input-field"
                  placeholder="Blog titel"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Introductie <span className="text-red-500">*</span>
                  <span className="text-gray-500 font-normal ml-2">({intro.length} karakters)</span>
                </label>
                <textarea
                  value={intro}
                  onChange={(e) => setIntro(e.target.value)}
                  className="textarea-field min-h-[100px]"
                  placeholder="Blog introductie"
                />
              </div>

              <FeaturedImageManager
                currentImageUrl={featuredImage}
                userId={user!.id}
                blogId={blogId!}
                onImageChange={(url) => {
                  setFeaturedImage(url);
                  scheduleAutoSave();
                }}
              />
            </div>
          </div>

          <div className="border-t border-gray-200 pt-6">
            <h2 className="text-xl font-semibold text-[#231F20] mb-4">Blog Inhoud</h2>

            <div className="border border-gray-300 rounded-lg overflow-hidden">
              <div className="bg-gray-50 border-b border-gray-300 p-2 flex flex-wrap gap-1 sticky top-0 z-10">
                <button
                  onClick={() => editor.chain().focus().toggleBold().run()}
                  className={`p-2 rounded hover:bg-gray-200 transition-colors ${
                    editor.isActive('bold') ? 'bg-gray-300' : ''
                  }`}
                  title="Bold"
                >
                  <Bold className="w-5 h-5" />
                </button>
                <button
                  onClick={() => editor.chain().focus().toggleItalic().run()}
                  className={`p-2 rounded hover:bg-gray-200 transition-colors ${
                    editor.isActive('italic') ? 'bg-gray-300' : ''
                  }`}
                  title="Italic"
                >
                  <Italic className="w-5 h-5" />
                </button>
                <button
                  onClick={() => editor.chain().focus().toggleUnderline().run()}
                  className={`p-2 rounded hover:bg-gray-200 transition-colors ${
                    editor.isActive('underline') ? 'bg-gray-300' : ''
                  }`}
                  title="Underline"
                >
                  <UnderlineIcon className="w-5 h-5" />
                </button>
                <div className="relative" ref={colorPickerRef}>
                  <button
                    onClick={() => setShowColorPicker(!showColorPicker)}
                    className={`p-2 rounded hover:bg-gray-200 transition-colors flex items-center gap-1 ${
                      editor.isActive('highlight') ? 'bg-gray-300' : ''
                    }`}
                    title="Highlight"
                  >
                    <Highlighter className="w-5 h-5" />
                    <div
                      className="w-3 h-3 rounded-sm border border-gray-400"
                      style={{ backgroundColor: activeHighlightColor }}
                    />
                    <ChevronDown className="w-3 h-3" />
                  </button>
                  {showColorPicker && (
                    <div className="absolute top-full left-0 mt-1 bg-white border border-gray-300 rounded-lg shadow-lg p-2 z-20 min-w-[200px]">
                      <div className="grid grid-cols-4 gap-2 mb-2">
                        {highlightColors.map((color) => (
                          <button
                            key={color.color}
                            onClick={() => applyHighlight(color.color)}
                            className="w-10 h-10 rounded border-2 border-gray-300 hover:border-gray-500 transition-colors"
                            style={{ backgroundColor: color.color }}
                            title={color.name}
                          />
                        ))}
                      </div>
                      <button
                        onClick={removeHighlight}
                        className="w-full px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded flex items-center justify-center gap-2 transition-colors"
                      >
                        <X className="w-4 h-4" />
                        Verwijder highlight
                      </button>
                    </div>
                  )}
                </div>
                <div className="w-px bg-gray-300 mx-1" />
                <button
                  onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
                  className={`p-2 rounded hover:bg-gray-200 transition-colors ${
                    editor.isActive('heading', { level: 1 }) ? 'bg-gray-300' : ''
                  }`}
                  title="Heading 1"
                >
                  <Heading1 className="w-5 h-5" />
                </button>
                <button
                  onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
                  className={`p-2 rounded hover:bg-gray-200 transition-colors ${
                    editor.isActive('heading', { level: 2 }) ? 'bg-gray-300' : ''
                  }`}
                  title="Heading 2"
                >
                  <Heading2 className="w-5 h-5" />
                </button>
                <button
                  onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
                  className={`p-2 rounded hover:bg-gray-200 transition-colors ${
                    editor.isActive('heading', { level: 3 }) ? 'bg-gray-300' : ''
                  }`}
                  title="Heading 3"
                >
                  <Heading3 className="w-5 h-5" />
                </button>
                <div className="w-px bg-gray-300 mx-1" />
                <button
                  onClick={() => editor.chain().focus().toggleBulletList().run()}
                  className={`p-2 rounded hover:bg-gray-200 transition-colors ${
                    editor.isActive('bulletList') ? 'bg-gray-300' : ''
                  }`}
                  title="Bullet List"
                >
                  <List className="w-5 h-5" />
                </button>
                <button
                  onClick={() => editor.chain().focus().toggleOrderedList().run()}
                  className={`p-2 rounded hover:bg-gray-200 transition-colors ${
                    editor.isActive('orderedList') ? 'bg-gray-300' : ''
                  }`}
                  title="Numbered List"
                >
                  <ListOrdered className="w-5 h-5" />
                </button>
                <div className="w-px bg-gray-300 mx-1" />
                <button
                  onClick={() => editor.chain().focus().toggleBlockquote().run()}
                  className={`p-2 rounded hover:bg-gray-200 transition-colors ${
                    editor.isActive('blockquote') ? 'bg-gray-300' : ''
                  }`}
                  title="Quote"
                >
                  <Quote className="w-5 h-5" />
                </button>
                <button
                  onClick={openLinkModal}
                  className={`p-2 rounded hover:bg-gray-200 transition-colors ${
                    editor.isActive('link') ? 'bg-gray-300' : ''
                  }`}
                  title="Link toevoegen of bewerken"
                >
                  <Link2 className="w-5 h-5" />
                </button>
                <button
                  onClick={images.openImagePicker}
                  className="p-2 rounded hover:bg-gray-200 transition-colors"
                  title="Afbeelding toevoegen"
                >
                  <ImagePlus className="w-5 h-5" />
                </button>
                <button
                  onClick={images.openGroupPicker}
                  className="p-2 rounded hover:bg-gray-200 transition-colors"
                  title="Beeldgroep toevoegen (meerdere foto's naast elkaar)"
                >
                  <LayoutGrid className="w-5 h-5" />
                </button>
                <div className="w-px bg-gray-300 mx-1" />
                <button
                  onClick={() => editor.chain().focus().undo().run()}
                  disabled={!editor.can().undo()}
                  className="p-2 rounded hover:bg-gray-200 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                  title="Ongedaan maken"
                >
                  <Undo className="w-5 h-5" />
                </button>
                <button
                  onClick={() => editor.chain().focus().redo().run()}
                  disabled={!editor.can().redo()}
                  className="p-2 rounded hover:bg-gray-200 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                  title="Opnieuw"
                >
                  <Redo className="w-5 h-5" />
                </button>
              </div>
              <div className="overflow-y-auto editor-scrollable-container blog-editor" style={{ maxHeight: '600px' }}>
                <EditorContent editor={editor} />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
      {showLinkModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-[#231F20] flex items-center gap-2">
                <Link2 className="w-5 h-5 text-[#FF6B35]" />
                {isEditingExistingLink ? 'Link bewerken' : 'Link invoegen'}
              </h2>
              <button
                onClick={() => setShowLinkModal(false)}
                className="p-1.5 rounded hover:bg-gray-100 transition-colors text-gray-500"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="px-6 py-5 space-y-4">
              {linkText && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Linktekst
                  </label>
                  <div className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-600 break-all">
                    {linkText}
                  </div>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  URL <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={linkUrl}
                  onChange={(e) => setLinkUrl(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') handleLinkSave(); if (e.key === 'Escape') setShowLinkModal(false); }}
                  className="input-field"
                  placeholder="https://voorbeeld.nl"
                  autoFocus
                />
                {linkUrl && !linkUrl.startsWith('http') && (
                  <p className="mt-1 text-xs text-gray-500">
                    Wordt automatisch aangevuld met https://
                  </p>
                )}
              </div>
            </div>

            <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between gap-3">
              <div>
                {isEditingExistingLink && (
                  <button
                    onClick={handleLinkRemove}
                    className="flex items-center gap-1.5 px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                  >
                    <Link2Off className="w-4 h-4" />
                    Link verwijderen
                  </button>
                )}
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setShowLinkModal(false)}
                  className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  Annuleren
                </button>
                <button
                  onClick={handleLinkSave}
                  disabled={!linkUrl.trim()}
                  className="px-4 py-2 text-sm font-medium bg-[#FF6B35] text-white rounded-lg hover:bg-[#E55A2B] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  {isEditingExistingLink ? 'Opslaan' : 'Link invoegen'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      <EditorImageTools {...images.toolsProps} />
    </>
  );
}
